import React from 'react';
import { Partner } from '@/types/xdao';
import { Link } from 'wouter';
import { formatDateRange } from '@/lib/utils';
import { getDateRangeStatus } from '@/utils/TimeUtils';

function PartnerCard({
  partner,
  useClientRouterLink = true,
}: {
  partner: Partner;
  useClientRouterLink?: boolean;
}) {
  const dateRange = formatDateRange(partner.thumbnailStartDate, partner.thumbnailEndDate);

  // Check if today is within the date range
  const dateStatus = getDateRangeStatus(
    partner.thumbnailStartDate,
    partner.thumbnailEndDate
  );

  const cardClassName = "w-full flex justify-start items-start flex-col gap-3 bg-[#171921] rounded-2xl text-start overflow-hidden";

  const cardContent = (
    <>
      <div className="w-full md:h-[14.375rem] overflow-hidden">
        <img
          src={partner.thumbnailImage}
          className='w-full h-full object-cover'
          alt={`${partner.thumbnailTitle} badge`}
        />
      </div>
      <div className="px-[1.25rem] pb-[1.25rem] flex flex-col gap-4">
        <div className="flex self-stretch justify-start items-center flex-row gap-2">
          <p
          className="flex-1 text-[#FFFFFF] text-2xl font-['Tiny5'] leading-7 tracking-[0.0625rem]">
          {partner.thumbnailTitle}
          </p>
          {partner.isTestMode && (
            <span 
              className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-semibold rounded border border-yellow-500/40"
              title="This project is in test mode"
            >
              🧪 Early Stage
            </span>
          )}
        </div>
        <div className="flex self-stretch justify-start items-center flex-row">
          <p className="text-[#38DD85] text-xs font-['Inter'] font-semibold leading-5">
            {dateStatus === 'live'
              ? 'Live'
              : dateStatus === 'active'
                ? dateRange
                : dateStatus === 'expired'
                  ? 'Ended'
                  : dateStatus === 'available-soon'
                    ? 'Available Soon'
                    : 'Coming soon'}
          </p>
        </div>
        <div className="flex self-stretch justify-start items-start flex-col">
          {partner.thumbnailDescription && (
            <p className="flex-1 text-[#757575] text-sm font-['Inter'] leading-6 line-clamp-3">
              {partner.thumbnailDescription}
            </p>
          )}
        </div>
      </div>
    </>
  );

  if (!['active', 'available-soon', 'expired', 'live'].includes(dateStatus)) {
    return (
      <div className={cardClassName} style={{ cursor: 'not-allowed' }}>
        {cardContent}
      </div>
    );
  }

  const href = `/xdao/${partner.slug}`;

  return useClientRouterLink ? (
    <Link to={href} className={cardClassName}>
      {cardContent}
    </Link>
  ) : (
    <a href={href} className={cardClassName}>
      {cardContent}
    </a>
  );
}

export default PartnerCard;
